import React, { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { PRODUCTS } from '../data/products';
import BottleImage from './BottleImage';
import { reveal, registerScrollTrigger } from '../lib/motion';
import { whatsappLinkProps, productOrderMessage } from '../lib/whatsapp';

// A deliberately spread selection: gold, amber, plum, brown, mahogany, ruby.
const FEATURED_IDS = [4, 1, 7, 6, 11, 3];

const ShopPreview = () => {
  const sectionRef = useRef(null);
  const titleRef   = useRef(null);
  const cardsRef   = useRef(null);
  const [showAll, setShowAll] = useState(false);

  const displayProducts = showAll ? PRODUCTS : PRODUCTS.filter(p => FEATURED_IDS.includes(p.id));

  useEffect(() => {
    registerScrollTrigger();
    const ctx = gsap.context(() => {
      reveal(titleRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, ease: 'power2.out', scrollTrigger: { trigger: titleRef.current, start: 'top 80%' } }
      );
      const cards = cardsRef.current?.querySelectorAll('.shop-card');
      if (cards?.length) {
        reveal(cards,
          { opacity: 0, y: 50 },
          { opacity: 1, y: 0, stagger: 0.1, duration: 0.9, ease: 'power2.out', scrollTrigger: { trigger: cardsRef.current, start: 'top 80%' } }
        );
      }
    }, sectionRef);
    return () => ctx.revert();
    // `displayProducts` is a fresh array every render; keying off the toggle
    // itself is what actually changes the card set.
  }, [showAll]);

  return (
    <section
      id="shop"
      ref={sectionRef}
      data-testid="shop-section"
      aria-label="Shop the scents"
      className="section-pad"
      style={{ background: 'var(--black)' }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        <div ref={titleRef} style={{ opacity: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '80px', flexWrap: 'wrap', gap: '24px' }}>
          <div>
            <span className="section-eyebrow">05 — Shop</span>
            <h2 style={{ fontFamily: 'Cinzel', fontStyle: 'normal', fontSize: 'clamp(28px, 4.5vw, 52px)', letterSpacing: '5px', color: 'var(--cream)', fontWeight: 400, marginTop: '18px' }}>
              The Scents
            </h2>
          </div>
          <p style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '17px', color: 'var(--grey)', maxWidth: '360px', lineHeight: 1.7 }}>
            Each fragrance crafted to rival the world's finest houses — at a price that respects you.
          </p>
        </div>

        <div ref={cardsRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '2px' }}>
          {displayProducts.map((product) => (
            <div
              key={product.id}
              className="shop-card product-card-wrapper"
              data-testid={`shop-card-${product.id}`}
              style={{ background: 'var(--deep-warm-black)', padding: '40px 30px 32px', opacity: 0 }}
            >
              <div style={{ height: '150px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginBottom: '28px' }}>
                <BottleImage product={product} height={150} />
              </div>

              {/* Two-line reserve, same reason as the collection cards: the
                  longer brand names wrap and would otherwise knock the product
                  name and price out of line across the grid. */}
              <span style={{ fontFamily: 'Tenor Sans', fontStyle: 'normal', fontSize: '10px', letterSpacing: '1.8px', textTransform: 'uppercase', color: 'var(--gold-dark)', display: 'block', marginBottom: '8px', lineHeight: 1.5, minHeight: '3em' }}>
                {product.inspired_by}
              </span>
              <h3 style={{ fontFamily: 'Cinzel', fontStyle: 'normal', fontSize: '17px', letterSpacing: '3px', color: 'var(--cream)', marginBottom: '6px', fontWeight: 400, lineHeight: 1.3 }}>
                {product.name}
              </h3>
              <p style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '14px', color: 'var(--grey)', marginBottom: '6px', lineHeight: 1.5 }}>
                {product.type} · {product.size}
              </p>
              <p style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '13px', color: 'var(--grey-dim)', marginBottom: '20px' }}>
                100mL available upon request.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', paddingTop: '16px', borderTop: '1px solid rgba(201,169,110,0.08)' }}>
                <span style={{ fontFamily: 'Cinzel', fontStyle: 'normal', fontSize: '16px', color: 'var(--gold-light)' }}>
                  $100 <span style={{ fontFamily: 'Tenor Sans', fontSize: '10px', color: 'var(--grey)' }}>CAD</span>
                </span>
                <a
                  {...whatsappLinkProps(productOrderMessage(product))}
                  className="btn-gold"
                  data-testid={`shop-now-btn-${product.id}`}
                  aria-label={`Order ${product.name} via WhatsApp`}
                  style={{ padding: '10px 18px', fontSize: '10px', letterSpacing: '2.5px' }}
                >
                  Order Now
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Toggle show all */}
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <button
            onClick={() => setShowAll(v => !v)}
            className="btn-gold"
            data-testid="toggle-all-scents-btn"
            aria-expanded={showAll}
            style={{ background: 'none', border: '1px solid rgba(201,169,110,0.35)', cursor: 'pointer' }}
          >
            {showAll ? 'Show Less' : `View All ${PRODUCTS.length} Scents`}
          </button>
        </div>

        <div style={{ marginTop: '40px', textAlign: 'center' }}>
          <p style={{ fontFamily: 'Cormorant Garamond', fontStyle: 'italic', fontSize: '14px', color: 'var(--grey-dim)' }}>
            All fragrances are original compositions inspired by designer scents. Not affiliated with any designer brand.
          </p>
        </div>

      </div>
    </section>
  );
};

export default ShopPreview;
